/**
 * Funnel Trends View — answers Leitfragen 1, 2, 2b, 3, 5, 6.
 *
 * Loads six independent trend endpoints in parallel (Promise.allSettled so a
 * single slow Plausible call doesn't block the others) and renders each as
 * its own card with a dedicated chart.
 *
 * Endpoints (all under /admin/analytics):
 *   - funnel/active-demo-trend         → BarLineComboChart  (LF1)
 *   - landingpage-sources-trend        → StackedAreaChart   (LF2)
 *   - internal-source-conversion       → TimeSeriesChart    (LF2b)
 *   - engagement-trend                 → TimeSeriesChart    (LF3)
 *   - trade-fair-trend                 → BarLineComboChart  (LF5)
 *   - cumulative-active-demos          → StackedAreaChart   (LF6, cumulative)
 */
import { useEffect, useState } from 'preact/hooks';
import {
  BarLineComboChart,
  StackedAreaChart,
  TimeSeriesChart,
} from './shared/ChartComponents.jsx';
import { COLORS } from './shared/chartTheme.js';
import { InfoTooltip } from './shared/InfoTooltip.jsx';
import {
  getActiveDemoTrend,
  getLandingpageSourcesTrend,
  getInternalSourceConversion,
  getEngagementTrend,
  getFunnelSubStages,
  getTradeFairTrend,
  getCumulativeActiveDemos,
} from '../../../services/adminService.js';

// Heartbeat sessions (user_sessions) — the token-independent activity signal
// used by the active-demo metrics — only exist from this date on. Charts shade
// the period before it (last_login fallback, undercounts) and draw a boundary.
const HEARTBEAT_RELIABLE_FROM = '2026-05-18';

function SectionWrapper({ title, subtitle, children, error, loading, infoKey }) {
  return (
    <section className="funnel-trends-section" style={{ marginBottom: 32 }}>
      <header style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0, color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          {title}
          {infoKey && <InfoTooltip infoKey={infoKey} />}
        </h3>
        {subtitle && (
          <p style={{ margin: '4px 0 0', color: COLORS.gray, fontSize: 12 }}>{subtitle}</p>
        )}
      </header>
      {loading && <div style={{ color: COLORS.gray, fontSize: 12 }}>Loading…</div>}
      {error && !loading && (
        <div style={{ color: COLORS.red, fontSize: 12 }}>
          Failed to load: {String(error.message || error)}
        </div>
      )}
      {!loading && !error && children}
    </section>
  );
}

export function AnalyticsFunnelTrendsView({ periodDays = 90, bucket = 'week', excludeAdmin = true }) {
  const [state, setState] = useState({
    activeDemo: { loading: true, error: null, data: null },
    sources: { loading: true, error: null, data: null },
    internal: { loading: true, error: null, data: null },
    engagement: { loading: true, error: null, data: null },
    subStages: { loading: true, error: null, data: null },
    tradeFair: { loading: true, error: null, data: null },
    cumulative: { loading: true, error: null, data: null },
  });

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      const setOne = (key, patch) => {
        if (cancelled) return;
        setState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
      };

      const calls = [
        ['activeDemo', () => getActiveDemoTrend({ periodDays, bucket, excludeAdmin })],
        ['sources', () => getLandingpageSourcesTrend({ periodDays, bucket })],
        ['internal', () => getInternalSourceConversion({ periodDays, bucket })],
        ['engagement', () => getEngagementTrend({ periodDays, bucket, excludeAdmin })],
        ['subStages', () => getFunnelSubStages({ periodDays, excludeAdmin })],
        ['tradeFair', () => getTradeFairTrend({ periodDays, bucket })],
        ['cumulative', () => getCumulativeActiveDemos({ periodDays, bucket })],
      ];

      // Reset all to loading first
      setState((s) => {
        const next = { ...s };
        for (const [key] of calls) {
          next[key] = { loading: true, error: null, data: null };
        }
        return next;
      });

      const results = await Promise.allSettled(calls.map(([_, fn]) => fn()));
      results.forEach((res, idx) => {
        const [key] = calls[idx];
        if (res.status === 'fulfilled') {
          setOne(key, { loading: false, error: null, data: res.value });
        } else {
          setOne(key, { loading: false, error: res.reason, data: null });
        }
      });
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [periodDays, bucket, excludeAdmin]);

  const subStagesData = state.subStages.data;
  // The LF4 cards are a single aggregate over the selected period and cannot
  // show the heartbeat shading the time-series charts use. Flag it when the
  // window reaches before the heartbeat start so the "Active Demo" number's
  // partial last_login fallback is transparent.
  const periodPredatesHeartbeat =
    Date.now() - periodDays * 86400000 < Date.parse(HEARTBEAT_RELIABLE_FROM);

  return (
    <div className="funnel-trends-view">
      {/* LF4 — Refined Funnel Sub-stages */}
      <SectionWrapper
        title="Refined Funnel: Demo Requested → Demo Accepted → Active Demo → PRO"
        subtitle="Sub-stages between Registration (email verified) and PRO Subscription."
        loading={state.subStages.loading}
        error={state.subStages.error}
        infoKey="trendsLF4SubStages"
      >
        {subStagesData && (
          <div className="funnel-substages-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {subStagesData.sub_stages.map((stage, idx) => {
              const conv = idx > 0 ? subStagesData.conversions[idx - 1] : null;
              return (
                <div
                  key={stage.stage_id}
                  className="funnel-stage-card funnel-stage-card--substep"
                  style={{
                    flex: '1 1 200px',
                    padding: 16,
                    border: `1px solid ${COLORS.grid}`,
                    borderRadius: 8,
                    background: COLORS.background,
                  }}
                >
                  <div style={{ color: COLORS.gray, fontSize: 11, textTransform: 'uppercase' }}>
                    {stage.label}
                  </div>
                  <div style={{ color: '#fff', fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>
                    {stage.value.toLocaleString('en-US')}
                  </div>
                  {conv != null && (
                    <div style={{ color: COLORS.cyan, fontSize: 12, marginTop: 4 }}>
                      {conv.rate}% from {conv.from_stage.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {subStagesData && periodPredatesHeartbeat && (
          <div
            style={{
              marginTop: 12,
              fontSize: 11.5,
              color: COLORS.gray,
              borderLeft: `3px solid ${COLORS.cyan}`,
              paddingLeft: 10,
              lineHeight: 1.5,
            }}
          >
            Period extends before heartbeat tracking began (2026-05-18): “Active Demo” is
            approximated via <span style={{ color: '#cbd5e1' }}>last_login</span> for demos
            activated before then (conservative); more recent activations are heartbeat-accurate.
          </div>
        )}
      </SectionWrapper>

      {/* LF1 */}
      <SectionWrapper
        title="New Active Demos & Conversion"
        subtitle="How many new active demos are won each period, and what share of landing-page visitors convert?"
        loading={state.activeDemo.loading}
        error={state.activeDemo.error}
        infoKey="trendsLF1ActiveDemo"
      >
        <BarLineComboChart
          title="New active demos per period (bars) vs. landing-page → active-demo rate (line)"
          data={state.activeDemo.data?.data || []}
          xKey="bucket_start"
          barKey="active_demos"
          barLabel="New active demos"
          barColor={COLORS.green}
          lineKey="lp_to_demo_rate"
          lineLabel="Conversion %"
          lineColor="#e2e8f0"
          yAxisLabels={{ left: 'New active demos', right: 'Conversion (%)' }}
          reliableFrom={HEARTBEAT_RELIABLE_FROM}
        />
      </SectionWrapper>

      {/* LF2 */}
      <SectionWrapper
        title="Landing Page Sources"
        subtitle="Which acquisition channels bring visitors to the Insights landing page over time?"
        loading={state.sources.loading}
        error={state.sources.error}
        infoKey="trendsLF2Sources"
      >
        <StackedAreaChart
          title="Landing-page visitors by acquisition channel"
          data={state.sources.data?.data || []}
          xKey="bucket_start"
          series={[
            { name: 'Google', key: 'google', color: '#dc2626' },
            { name: 'Bing', key: 'bing', color: COLORS.cyan },
            { name: 'LinkedIn', key: 'linkedin', color: COLORS.purple },
            { name: 'Direct', key: 'direct', color: COLORS.green },
            { name: 'Cell pages', key: 'cellpage', color: COLORS.orange },
            { name: 'Other', key: 'other', color: COLORS.gray },
          ]}
        />
      </SectionWrapper>

      {/* LF2b */}
      <SectionWrapper
        title="Internal Source Efficiency"
        subtitle="How effectively do Cell Explorer and Cell Pages funnel visitors to the Insights landing page (same-session)?"
        loading={state.internal.loading}
        error={state.internal.error}
        infoKey="trendsLF2bInternal"
      >
        <TimeSeriesChart
          title="Explorer→LP and Cells→LP conversion (%)"
          data={state.internal.data?.data || []}
          xKey="bucket_start"
          series={[
            { name: 'Explorer → LP', key: 'explorer_to_lp_rate', color: COLORS.cyan },
            { name: 'Cell pages → LP', key: 'cell_to_lp_rate', color: COLORS.orange },
          ]}
        />
      </SectionWrapper>

      {/* LF3 */}
      <SectionWrapper
        title="Demo Engagement Time"
        subtitle="Average active minutes per demo user (heartbeat-based; users without login in the period are excluded)."
        loading={state.engagement.loading}
        error={state.engagement.error}
        infoKey="trendsLF3Engagement"
      >
        <TimeSeriesChart
          title="Avg active minutes per demo user"
          data={state.engagement.data?.data || []}
          xKey="bucket_start"
          series={[
            { name: 'Avg minutes / user', key: 'avg_active_minutes', color: COLORS.green, area: true },
          ]}
        />
      </SectionWrapper>

      {/* LF5 */}
      <SectionWrapper
        title="Trade Fair Demos (booth proxy)"
        subtitle="Booth accounts created by the team (admin_created). Of each period's registrations, how many became active demos?"
        loading={state.tradeFair.loading}
        error={state.tradeFair.error}
        infoKey="trendsLF5TradeFair"
      >
        <BarLineComboChart
          title="Trade-fair active demos (booth cohort, bars) vs. activation rate (line)"
          data={state.tradeFair.data?.data || []}
          xKey="bucket_start"
          barKey="trade_fair_active_demos"
          barLabel="Active Demos (trade fair)"
          barColor={COLORS.orange}
          lineKey="trade_fair_conversion_rate"
          lineLabel="Activation %"
          lineColor={COLORS.cyan}
          yAxisLabels={{ left: 'Active Demos', right: 'Activation (%)' }}
          reliableFrom={HEARTBEAT_RELIABLE_FROM}
        />
      </SectionWrapper>

      {/* LF6 */}
      <SectionWrapper
        title="Cumulative Active Demos: Trade Fair vs. Organic"
        subtitle="Active-demo snapshot at each bucket end, split into booth accounts (admin_created) vs organic."
        loading={state.cumulative.loading}
        error={state.cumulative.error}
        infoKey="trendsLF6Cumulative"
      >
        <StackedAreaChart
          title="Active demos at end of bucket (trade fair = booth accounts)"
          data={state.cumulative.data?.data || []}
          xKey="bucket_start"
          series={[
            { name: 'Organic', key: 'organic', color: COLORS.cyan },
            { name: 'Trade Fair', key: 'trade_fair', color: COLORS.orange },
          ]}
          cumulative={false}
          reliableFrom={HEARTBEAT_RELIABLE_FROM}
        />
      </SectionWrapper>
    </div>
  );
}
